import { useState } from "react"

import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import gsap from "gsap"
import { useTranslation } from "react-i18next"
import styled from "styled-components"

import media from "styles/media"
import useAnimation from "utils/useAnimation"

interface Props {
  src: IGatsbyImageData | undefined
  alt: string
  height?: string
  width?: string
  hero?: boolean
  objectFit?: string
  borderRadius?: string
  className?: string
}

export default function ZoomImageGatsby({
  src,
  alt,
  height = "",
  width = "",
  objectFit = "cover",
  borderRadius = "",
  hero = false,
  className = "",
}: Props) {
  const [imageWrap, setImageWrap] = useState<HTMLElement | null>(null)
  const { t } = useTranslation()

  useAnimation(() => {
    if (imageWrap) {
      gsap.to(imageWrap, {
        scale: 1.2,
        scrollTrigger: {
          trigger: imageWrap,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      })
    }
  }, [imageWrap])

  return (
    <ImageWrap
      className={className}
      imgHeight={height}
      imgWidth={width}
      imgBorderR={borderRadius}
    >
      <ExpandWrap ref={ref => setImageWrap(ref)}>
        {src && (
          <Image
            image={src}
            alt={t(alt)}
            $objectFit={objectFit}
            loading={hero ? "eager" : "lazy"}
          />
        )}
      </ExpandWrap>
    </ImageWrap>
  )
}

const ImageWrap = styled.div<{
  imgHeight?: string
  imgWidth?: string
  imgBorderR?: string
}>`
  overflow: hidden;
  width: ${props => props.imgWidth && props.imgWidth};
  height: ${props => props.imgHeight && props.imgHeight};
  will-change: transform;
  z-index: 1;
  position: relative;
  transform: translate3d(0, 0, 0, 0);
  border-radius: ${props => props.imgBorderR};
`

const ExpandWrap = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  transform-origin: 50% 50%;
`

const Image = styled(GatsbyImage)<{ $objectFit: string }>`
  width: 100%;
  height: 100%;

  img {
    object-fit: ${({ $objectFit }) => $objectFit} !important;
  }
  object-fit: ${({ $objectFit }) => $objectFit} !important;
  ${media.tablet} {
    height: ${({ $objectFit }) => ($objectFit === "crop" ? "initial" : "100%")};

    width: 100%;
  }

  ${media.mobile} {
    position: relative;
  }
`
